// client/src/coms/views/CommsHomeView.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginButton from '../components/LoginButton';
import { SavedAccountsPicker } from '../accounts/AccountSwitcher';
import { comsApiGet } from '../comsApi';
import { joinByInvite } from '../e2ee/EncryptionConsentHost';

const LS_LAST_SERVER_ID = 'lastCommsServerId';

function isOnVoxemeraHost() {
  try {
    if (typeof window === 'undefined') return false;
    const h = String(window.location.hostname || '').toLowerCase();
    return h === 'voxemera.com' || h.endsWith('.voxemera.com');
  } catch {
    return false;
  }
}

function readLastServerId() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return String(window.localStorage.getItem(LS_LAST_SERVER_ID) || '').trim();
  } catch {
    return '';
  }
}

function getServerKey(obj) {
  const s = obj && typeof obj === 'object' ? obj : {};
  return String(
    s.server_key != null
      ? s.server_key
      : s.public_id != null
        ? s.public_id
        : s.server_id != null
          ? s.server_id
          : s.serverId != null
            ? s.serverId
            : s.id != null
              ? s.id
              : ''
  ).trim();
}

function pickServerIdFromPayload(p) {
  const obj = p && typeof p === 'object' ? p : {};

  const direct =
    obj.server_key != null
      ? String(obj.server_key)
      : obj.public_id != null
        ? String(obj.public_id)
        : obj.server_id != null
          ? String(obj.server_id)
          : obj.serverId != null
            ? String(obj.serverId)
            : obj.id != null
              ? String(obj.id)
              : obj.server && obj.server.server_key != null
                ? String(obj.server.server_key)
                : obj.server && obj.server.public_id != null
                  ? String(obj.server.public_id)
                  : obj.server && obj.server.server_id != null
                    ? String(obj.server.server_id)
                    : obj.server && obj.server.serverId != null
                      ? String(obj.server.serverId)
                      : obj.server && obj.server.id != null
                        ? String(obj.server.id)
                        : '';

  const out = String(direct || '').trim();
  if (out) return out;

  const list = Array.isArray(obj.servers) ? obj.servers : [];
  if (list.length) {
    const first = list[0] || {};
    return getServerKey(first) || null;
  }

  return null;
}

function pickRememberedServerIdFromPayload(p) {
  const saved = readLastServerId();
  if (!saved) return '';

  const obj = p && typeof p === 'object' ? p : {};
  const list = Array.isArray(obj.servers) ? obj.servers : [];

  if (!list.length) return '';

  const found = list.find((s) => getServerKey(s) === saved);
  return found ? getServerKey(found) : '';
}

function isProbably401(err) {
  try {
    const s = err && typeof err === 'object' ? err.status : 0;
    if (s === 401) return true;
    const msg = String((err && err.message) || err || '');
    return msg.includes('401') || msg.toLowerCase().includes('not authenticated');
  } catch {
    return false;
  }
}

function BrandMark({ size = 40, radius = 14 }) {
  const isVox = isOnVoxemeraHost();
  const candidates = [
    isVox ? (process.env.PUBLIC_URL || '') + '/vox-favicon-512x512.png' : '',
    isVox ? (process.env.PUBLIC_URL || '') + '/vox-favicon-256x256.png' : '',
    isVox ? (process.env.PUBLIC_URL || '') + '/vox-favicon-16x16-.png' : '',
    typeof window !== 'undefined' ? window.__PA_COMMS_LOGO_URL__ : '',
    (process.env.PUBLIC_URL || '') + '/point-atlas-logo.png',
    (process.env.PUBLIC_URL || '') + '/logo192.png',
  ].filter(Boolean);

  const [idx, setIdx] = useState(0);
  const src = candidates[idx] || '';

  const outer = {
    width: size,
    height: size,
    borderRadius: radius,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: 'linear-gradient(180deg, rgba(var(--vx-link-rgb, 120,170,255), 0.35), rgba(var(--vx-link-rgb, 120,170,255), 0.10))',
    boxShadow: '0 18px 55px rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flex: '0 0 auto',
  };

  const fallback = {
    fontWeight: 900,
    letterSpacing: '-0.02em',
    fontSize: Math.max(12, Math.floor(size * 0.42)),
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.95 * var(--vx-ov, 1)))',
  };

  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.35))',
  };

  if (!src) {
    return (
      <div style={outer}>
        <span style={fallback}>{isVox ? 'VX' : 'PA'}</span>
      </div>
    );
  }

  return (
    <div style={outer}>
      <img
        src={src}
        alt="Voxemera"
        style={imgStyle}
        onError={() => {
          const next = idx + 1;
          if (next < candidates.length) setIdx(next);
        }}
      />
    </div>
  );
}

export default function CommsHomeView({ user }) {
  const navigate = useNavigate();
  const isVox = useMemo(() => isOnVoxemeraHost(), []);

  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [apiDown, setApiDown] = useState(false);
  const [detail, setDetail] = useState('');

  const [joinInviteCode, setJoinInviteCode] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinErr, setJoinErr] = useState('');

  const brandTitle = isVox ? 'Voxemera' : 'Point Atlas';
  const brandSub = isVox ? 'by Point Atlas' : 'Comms';

  const isAuthed = useMemo(() => {
    const u = user && typeof user === 'object' ? user : null;
    if (!u) return false;
    if (u.id != null) return true;
    if (u.user_id != null) return true;
    if (String(u.username || '').trim()) return true;
    if (String(u.name || '').trim()) return true;
    if (String(u.email || '').trim()) return true;
    return false;
  }, [user]);

  useEffect(() => {
    if (!isVox) return;
    if (isAuthed) return;
    try {
      const p = String(window.location.pathname || '/').toLowerCase();
      if (p === '/comms' || p.startsWith('/comms/')) {
        navigate('/', { replace: true });
      }
    } catch {}
  }, [isAuthed, isVox, navigate]);

  const goIntoComms = useCallback(
    (serverId) => {
      const sid = String(serverId || '').trim();
      if (!sid) return;
      try {
        window.localStorage.setItem(LS_LAST_SERVER_ID, sid);
      } catch {}
      navigate(`/comms/${encodeURIComponent(sid)}`);
    },
    [navigate]
  );

  const probe = useCallback(async () => {
    if (!isAuthed) {
      setLoading(false);
      setAuthRequired(true);
      setApiDown(false);
      setDetail('');
      return;
    }

    setLoading(true);
    setAuthRequired(false);
    setApiDown(false);
    setDetail('');

    try {
      const payload = await comsApiGet('/my-servers');
      const remembered = pickRememberedServerIdFromPayload(payload);
      const first = pickServerIdFromPayload(payload);
      const target = remembered || first;

      if (target) {
        goIntoComms(target);
        return;
      }

      setLoading(false);
    } catch (e) {
      if (isProbably401(e)) {
        setAuthRequired(true);
        setApiDown(false);
        setDetail('');
        setLoading(false);
        return;
      }

      setAuthRequired(false);
      setApiDown(true);
      setDetail(String((e && e.message) || e || 'Comms API unavailable'));
      setLoading(false);
    }
  }, [goIntoComms, isAuthed]);

  useEffect(() => {
    probe().catch(() => {
      setLoading(false);
      setApiDown(true);
      setDetail('Comms API unavailable');
    });
  }, [probe]);

  const handleJoinServer = useCallback(async () => {
    const code = String(joinInviteCode || '').trim();
    if (!code || joinBusy) return;

    setJoinBusy(true);
    setJoinErr('');

    try {
      const payload = await joinByInvite({ invite_code: code });
      const nextId = pickServerIdFromPayload(payload);
      if (!nextId) {
        setJoinErr('Invite worked, but no server was returned.');
        setJoinBusy(false);
        return;
      }

      try {
        window.localStorage.setItem(LS_LAST_SERVER_ID, String(nextId));
      } catch {}

      navigate(`/comms/${encodeURIComponent(String(nextId))}`);
    } catch (e) {
      setJoinErr(String((e && e.message) || e || 'Could not join server'));
      setJoinBusy(false);
    }
  }, [joinInviteCode, joinBusy, navigate]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background:
            'radial-gradient(1200px 700px at 20% -10%, rgba(90,140,255,0.16), transparent 55%), radial-gradient(1000px 680px at 100% 0%, rgba(40,210,190,0.12), transparent 50%), linear-gradient(180deg, #0a0d14 0%, #090b11 100%)',
          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.94 * var(--vx-ov, 1)))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <BrandMark size={48} radius={16} />
          <div>
            <div style={{ fontWeight: 900, fontSize: 22 }}>{brandTitle}</div>
            <div style={{ opacity: 0.75, fontSize: 14 }}>Loading your comms…</div>
          </div>
        </div>
      </div>
    );
  }

  if (authRequired) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          background:
            'radial-gradient(1200px 700px at 20% -10%, rgba(90,140,255,0.16), transparent 55%), radial-gradient(1000px 680px at 100% 0%, rgba(40,210,190,0.12), transparent 50%), linear-gradient(180deg, #0a0d14 0%, #090b11 100%)',
          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.94 * var(--vx-ov, 1)))',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            borderRadius: 24,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.1 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
            boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
            padding: 28,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <BrandMark size={52} radius={18} />
            <div>
              <div style={{ fontSize: 28, fontWeight: 900 }}>{brandTitle}</div>
              <div style={{ opacity: 0.72 }}>{brandSub}</div>
            </div>
          </div>

          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Sign in to continue</div>
          <div style={{ opacity: 0.8, lineHeight: 1.5, marginBottom: 20 }}>
            You need to be signed in to access your servers, DMs, voice channels, and notifications.
          </div>

          <LoginButton />
          <SavedAccountsPicker />
        </div>
      </div>
    );
  }

  if (apiDown) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          background:
            'radial-gradient(1200px 700px at 20% -10%, rgba(90,140,255,0.16), transparent 55%), radial-gradient(1000px 680px at 100% 0%, rgba(40,210,190,0.12), transparent 50%), linear-gradient(180deg, #0a0d14 0%, #090b11 100%)',
          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.94 * var(--vx-ov, 1)))',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 620,
            borderRadius: 24,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.1 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
            boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
            padding: 28,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <BrandMark size={52} radius={18} />
            <div>
              <div style={{ fontSize: 28, fontWeight: 900 }}>{brandTitle}</div>
              <div style={{ opacity: 0.72 }}>{brandSub}</div>
            </div>
          </div>

          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Comms is unavailable</div>
          <div style={{ opacity: 0.82, lineHeight: 1.5, marginBottom: 10 }}>
            The comms API could not be reached right now.
          </div>
          {detail ? (
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                opacity: 0.78,
                background: 'rgba(0,0,0,0.24)',
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                borderRadius: 14,
                padding: 12,
              }}
            >
              {detail}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background:
          'radial-gradient(1200px 700px at 20% -10%, rgba(90,140,255,0.16), transparent 55%), radial-gradient(1000px 680px at 100% 0%, rgba(40,210,190,0.12), transparent 50%), linear-gradient(180deg, #0a0d14 0%, #090b11 100%)',
        color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.94 * var(--vx-ov, 1)))',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 640,
          borderRadius: 24,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.1 * var(--vx-ov, 1)))',
          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
          boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
          padding: 28,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <BrandMark size={52} radius={18} />
          <div>
            <div style={{ fontSize: 28, fontWeight: 900 }}>{brandTitle}</div>
            <div style={{ opacity: 0.72 }}>{brandSub}</div>
          </div>
        </div>

        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>No servers yet</div>
        <div style={{ opacity: 0.82, lineHeight: 1.5, marginBottom: 18 }}>
          Join a server with an invite code to get started.
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            value={joinInviteCode}
            onChange={(e) => setJoinInviteCode(e.target.value)}
            placeholder="Enter invite code"
            style={{
              flex: '1 1 260px',
              minWidth: 220,
              height: 44,
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.95 * var(--vx-ov, 1)))',
              padding: '0 14px',
              outline: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleJoinServer();
            }}
          />
          <button
            type="button"
            onClick={handleJoinServer}
            disabled={joinBusy || !String(joinInviteCode || '').trim()}
            style={{
              height: 44,
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
              background: 'linear-gradient(180deg, rgba(var(--vx-link-rgb, 120,170,255), 0.35), rgba(var(--vx-link-rgb, 120,170,255), 0.16))',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
              padding: '0 16px',
              fontWeight: 800,
              cursor: joinBusy ? 'default' : 'pointer',
              opacity: joinBusy ? 0.7 : 1,
            }}
          >
            {joinBusy ? 'Joining…' : 'Join server'}
          </button>
        </div>

        {joinErr ? (
          <div style={{ marginTop: 12, color: 'rgba(255,160,160,0.95)', fontSize: 14 }}>
            {joinErr}
          </div>
        ) : null}
      </div>
    </div>
  );
}